import { useState } from 'react';
import {
    AddressField,
    Box,
    CprField,
    Grid,
    GridItem,
    Link,
    Message,
    validateCpr
} from '@flos/react-ui';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { externalLinks } from '../../strings';
import { useHippoText } from '../../hooks/useHippoText';
import { useGetAIDCustomerStop } from '../../recoil/hooks/useGetAIDCustomerStop';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';
import { identificationAtom } from '../../recoil/atoms/identificationAtom';
import { isRequestPendingAtom } from '../../recoil/atoms/isRequestPendingAtom';
import { useUpdateDummyCustomerDoB } from '../../recoil/hooks/useUpdateDummyCustomerDoB';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { getCoverableFromAllOnsaCoverables } from '../../utils/coverablesUtil';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { isCprTooOldForFlavour } from '../../utils/validationUtil';
import WizardPagetemplate from '../../templates/WizardPagetemplate';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import OnsaProgressBar from '../../shared_components/OnsaProgressBar/OnsaProgressBar';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import CustomerStopModal from '../../shared_components/CustomerStopModal/CustomerStopModal';
import RemoveModal from '../../shared_components/RemoveModal/RemoveModal';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { YoungAccidentOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';

const removeModalTexts = (flavourName: string): { modalTitle: string; modalText: string } => {
    switch (flavourName) {
        case 'Ulykkesforsikring':
            return {
                modalTitle: 'DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-HEADER',
                modalText: 'DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-SUBHEADER'
            };
        case 'Ulykkesforsikring til unge':
            return {
                modalTitle: 'DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-HEADER',
                modalText: 'DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-SUBHEADER'
            };
        case 'Indboforsikring til unge':
            return {
                modalTitle: 'DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-HEADER',
                modalText: 'DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-SUBHEADER'
            };
        default:
            return { modalTitle: '', modalText: '' };
    }
};

const shouldShowRemoveModal = (
    currentCoverable: CoverableDTO | undefined,
    cpr: string
): boolean => {
    if (
        currentCoverable?.flavourName !== 'Ulykkesforsikring' &&
        currentCoverable?.flavourName !== 'Ulykkesforsikring til unge' &&
        currentCoverable?.flavourName !== 'Indboforsikring til unge'
    )
        return false;
    if ((currentCoverable as YoungAccidentOnsaDTO)?.insuranceForOtherPerson) return false;
    return isCprTooOldForFlavour(cpr, currentCoverable.flavourName);
};

const CprQuestionPage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const onsaNavigation = useRecoilValue(onsaProgressSelector);
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const { getAIDCustomerStop } = useGetAIDCustomerStop();
    const { updateDummyCustomerDoB } = useUpdateDummyCustomerDoB();
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const allCoverablesOnsa = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { lobCode = '', fixedId = '' } = currentProduct ?? {};
    const currentCoverable = getCoverableFromAllOnsaCoverables(allCoverablesOnsa, fixedId, lobCode);
    const [address, setAddress] = useState<string>('');
    const [zipCode, setZipCode] = useState<string>('');
    const [cprNr, setCprNr] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [showStopModal, setShowStopModal] = useState<boolean>(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const [isAddressValid, setIsAddresValid] = useState<boolean | undefined>(false);
    const isCprNrValid = cprNr ? validateCpr(cprNr) : undefined;
    const getHippoText = useHippoText();
    const setIdentificationInfo = useSetRecoilState(identificationAtom);

    const handleCprChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setCprNr(value?.replace(/[\s-]/g, ''));
    };

    const goToInfoProcessing = (): void => {
        window.open(externalLinks.infoProcessing);
    };

    const isFormValid = !!cprNr && isCprNrValid && !!address && isAddressValid && !isLoading;

    const goNext = (): void => {
        setIsLoading(true);
        void getAIDCustomerStop(cprNr, zipCode).then((response) => {
            setStopCause(response.stopCause);
            if (response.stopCause !== 'None') {
                setShowStopModal(true);
                setIsLoading(false);
                return;
            }
            void updateDummyCustomerDoB(cprNr).then(() => {
                setIdentificationInfo({ cpr: cprNr, zipCode });
                shouldShowRemoveModal(currentCoverable, cprNr)
                    ? setShowRemoveModal(true)
                    : wizardGotoStep(onsaNavigation.nextStep);
                setIsLoading(false);
            });
        });
    };

    const goPrevious = (): void => {
        wizardGotoStep(onsaNavigation.prevStep);
    };

    if (showRemoveModal && currentCoverable) {
        return (
            <RemoveModal
                title={getHippoText(removeModalTexts(currentProduct?.flavourName).modalTitle)}
                description={getHippoText(removeModalTexts(currentProduct?.flavourName).modalText)}
                coverable={currentCoverable}
                showCloseButton={false}
                isRemoveAndContinueModal
                showModal={showRemoveModal}
                closeModal={() => {
                    setShowRemoveModal(false);
                }}
            />
        );
    }

    return (
        <>
            <OnsaProgressBar />
            <WizardPagetemplate
                onNext={goNext}
                onPrevious={goPrevious}
                disableNext={!isFormValid || isRequestPending}
                disablePrevious={isRequestPending}
            >
                <Grid>
                    <GridItem span={8} colStart={3}>
                        <Box padding="large" className="u-spacing-stack-l">
                            <h4>{getHippoText('DSS-CPR-PAGE-HEADER')}</h4>
                            <p>{getHippoText('DSS-CPR-PAGE-SUBHEADER')}</p>
                            <AddressField
                                onValidityChange={(isValid) => {
                                    setIsAddresValid(isValid);
                                }}
                                fieldEvent={(object: FieldEventDataProps) => {
                                    setAddress(object.address);
                                    setZipCode(object.addressInfo.zipCode);
                                }}
                            />
                            <CprField
                                label={getHippoText('DSS-CPR-PAGE-CPR-NUMBER')}
                                value={cprNr}
                                onChange={handleCprChange}
                                isValid={isCprNrValid}
                                required
                            />
                            <Message theme="secondary">
                                <p>{getHippoText('DSS-CPR-PAGE-DISCLAIMER')}</p>
                                <Link theme="logo" onClick={goToInfoProcessing}>
                                    {getHippoText('DSS-CPR-PAGE-INFO-PROCESSING')}
                                </Link>
                                <br></br>
                                <br></br>
                                <Link
                                    theme="logo"
                                    onClick={() => {
                                        setShowInfoModal(true);
                                    }}
                                >
                                    {getHippoText('DSS-CPR-PAGE-CALL-LINK')}
                                </Link>
                            </Message>
                        </Box>
                    </GridItem>
                </Grid>
                {showInfoModal && (
                    <CallMeModal
                        activityCode="dss_call_request"
                        showModal={showInfoModal}
                        closeModal={() => {
                            setShowInfoModal(false);
                        }}
                    />
                )}
            </WizardPagetemplate>
            <CustomerStopModal
                stopCause={stopCause}
                showModal={showStopModal}
                closeModal={() => {
                    setShowStopModal(false);
                }}
            />
        </>
    );
};

export default CprQuestionPage;
